import React from 'react';
import PropTypes from 'prop-types';
import { FaPen, FaTrash } from 'react-icons/fa';
import CustomRuleEditor from './CustomRuleEditor';
import { build_rule_summary } from './gen_config_utils';

// One custom generation rule: the summary line (which is also the name the
// generated checklists carry), an enable toggle, and edit / delete actions.
// While editing, the editor expands inline below the summary.
function CustomRuleCard({ rule, meta, saving, editing, onChange, onDelete, onToggleEdit }) {
  const summary = build_rule_summary(rule, meta);
  return (
    <div
      className="rounded-lg border border-gray-200 bg-gray-50/40 p-3"
      style={{ borderLeft: `3px solid ${rule.enabled ? '#7c3aed' : '#9ca3af'}` }}
    >
      <div className="flex items-center gap-3">
        <label
          className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none"
          style={{ color: 'var(--text-muted)', marginBottom: 0, flexShrink: 0 }}
          title="Enabled rules run in the daily generation job"
        >
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={() => onChange({ ...rule, enabled: !rule.enabled })}
            disabled={saving}
          />
          Active
        </label>
        <span
          className="flex-1 text-sm font-medium"
          style={{ color: rule.enabled ? 'var(--text-primary)' : 'var(--text-muted)' }}
        >
          {summary}
        </span>
        <div
          role="button"
          tabIndex={0}
          aria-label="Edit rule"
          onClick={() => !saving && onToggleEdit()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !saving) {
              e.preventDefault();
              onToggleEdit();
            }
          }}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer hover:bg-blue-50 ${
            editing ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
          }`}
        >
          <FaPen size={10} /> {editing ? 'Close' : 'Edit'}
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-label="Delete rule"
          onClick={() => !saving && onDelete()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !saving) {
              e.preventDefault();
              onDelete();
            }
          }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-600 cursor-pointer hover:bg-red-50"
        >
          <FaTrash size={10} /> Delete
        </div>
      </div>
      {editing ? (
        <CustomRuleEditor rule={rule} meta={meta} saving={saving} onChange={onChange} />
      ) : null}
    </div>
  );
}

CustomRuleCard.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.string,
    enabled: PropTypes.bool,
    condition: PropTypes.string,
    filters: PropTypes.shape({})
  }).isRequired,
  meta: PropTypes.shape({}).isRequired,
  saving: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func.isRequired
};

export default CustomRuleCard;
